import { Button } from "@d12labs/ui/button";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Check, Pencil, X } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { meetingPerfApi } from "../api";

const pad = (n: number) => String(n).padStart(2, "0");

/** A Date → the `YYYY-MM-DDTHH:mm` local-time string a datetime-local input takes. */
export function toLocalInputValue(d: Date): string {
	return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * The meeting date in the page header, editable in place. Pasted transcripts
 * are stamped with the day they were pasted, which misplaces them in lists and
 * trends — click the date to correct it. Read-only until clicked.
 */
export function MeetingDateEditor({ meetingId, value }: { meetingId: string; value: Date }) {
	const qc = useQueryClient();
	const [editing, setEditing] = useState(false);
	const [draft, setDraft] = useState("");

	const save = useMutation({
		mutationFn: (meetingDate: Date) =>
			meetingPerfApi.updateMeeting({
				params: { id: meetingId },
				input: { meetingDate: meetingDate.toISOString() },
			}),
		onSuccess: () => {
			toast.success("Meeting date updated");
			setEditing(false);
			// The date feeds lists, week numbers and speaker trends — refresh all of it.
			qc.invalidateQueries({ queryKey: ["meeting-perf"] });
		},
	});

	const label = value.toLocaleDateString(undefined, {
		weekday: "short",
		month: "short",
		day: "numeric",
		year: "numeric",
	});

	if (!editing) {
		return (
			<button
				type="button"
				title="Change meeting date"
				className="group inline-flex items-center gap-1 rounded-sm hover:text-ink focus-visible:text-ink"
				onClick={() => {
					setDraft(toLocalInputValue(value));
					setEditing(true);
				}}
			>
				{label}
				<Pencil className="size-3 opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100" />
			</button>
		);
	}

	const parsed = draft ? new Date(draft) : null;
	const valid = parsed != null && !Number.isNaN(parsed.getTime());
	const submit = () => {
		if (valid && parsed) save.mutate(parsed);
	};

	return (
		<span className="inline-flex items-center gap-1 align-middle">
			<input
				type="datetime-local"
				aria-label="Meeting date and time"
				// biome-ignore lint/a11y/noAutofocus: the field appears on explicit click — focus belongs in it
				autoFocus
				value={draft}
				max={toLocalInputValue(new Date())}
				onChange={(e) => setDraft(e.target.value)}
				onKeyDown={(e) => {
					if (e.key === "Enter") submit();
					if (e.key === "Escape") setEditing(false);
				}}
				className="h-7 rounded-md border border-hairline-strong bg-surface-1 px-2 text-body-sm text-ink tabular-nums outline-none focus:border-primary/60"
			/>
			<Button
				type="button"
				variant="ghost"
				size="icon-sm"
				title="Save date"
				disabled={!valid || save.isPending}
				onClick={submit}
			>
				<Check className="size-4" />
			</Button>
			<Button
				type="button"
				variant="ghost"
				size="icon-sm"
				title="Cancel"
				disabled={save.isPending}
				onClick={() => setEditing(false)}
			>
				<X className="size-4" />
			</Button>
		</span>
	);
}
